import type { DataContext } from '../types/aiChatSource'

export const BASE_ACTION_COSTS = {
  AI_CHAT: 2,
  SIMPLE_EDIT: 10,
  GENERATION: 25,
  ADVANCED_GENERATION: 50,
} as const

export type ActionCostKey = keyof typeof BASE_ACTION_COSTS | string

export const FIXED_INGEST_COSTS = {
  spreadsheet: 5,
  document: 5,
  gsheet: 3,
  link: 2,
  image: 15,
  upload: 0,
  asset: 0,
  design: 0,
  chat: 0,
  form: 0,
  form_responses: 0,
} as const

export type ContextCostEstimate = {
  fixedIngestCr: number
  tokenRange: { low: number; high: number }
  baseSendCr: number
  upperBound: number
}

export const getFixedIngestFeeForKind = (kind: string): number => {
  return FIXED_INGEST_COSTS[kind as keyof typeof FIXED_INGEST_COSTS] ?? 0
}

/**
 * Estimates the credit cost envelope for attaching context items to an AI run.
 * Follows the hybrid model: sum of per-source fixed ingest fees + dynamic token range + base action cost.
 */
export const estimateContextCost = (
  contexts: DataContext[],
  actionOrBaseCr: ActionCostKey | number = 'GENERATION',
  imageCount = 0,
): ContextCostEstimate => {
  const baseSendCr =
    typeof actionOrBaseCr === 'number'
      ? actionOrBaseCr
      : (BASE_ACTION_COSTS as Record<string, number>)[actionOrBaseCr] ?? 25

  let fixedIngestCr = imageCount * FIXED_INGEST_COSTS.image
  let totalChars = 0

  for (const ctx of contexts) {
    fixedIngestCr += ctx.fixedIngestCr ?? getFixedIngestFeeForKind(ctx.kind)
    if (ctx.text) {
      totalChars += ctx.text.length
    } else if (ctx.messages?.length) {
      totalChars += ctx.messages.reduce((sum, m) => sum + (m.textContent?.length || 0), 0)
    }
  }

  // Approx. 4 chars per token; ~2,000 tokens (8,000 chars) ~ 1 credit
  const low = totalChars > 0 ? Math.max(1, Math.ceil(totalChars / 8000)) : 0
  const high = Math.max(low, 2 * low)
  const upperBound = fixedIngestCr + high + baseSendCr

  return {
    fixedIngestCr,
    tokenRange: { low, high },
    baseSendCr,
    upperBound,
  }
}
